/**
 * I passi del tutorial guidato dell'elenco Resi.
 *
 * Due preparativi: il menu del piu' in alto e il menu della riga.
 * `stopReturnsListTour` li richiude a fine giro.
 *
 * I bersagli della pagina sono scoped; le voci dei menu no, perche' Vuetify
 * stacca il contenuto dei menu e lo appende a `[data-app]`, fuori dalla pagina.
 */
const at = name => `[data-tour-scope="returns-list"] [data-tour="${name}"]`;

const ROW_EDIT = '[data-tour="row-edit"]';

function isVisible(selector) {
	const element = document.querySelector(selector);

	if (!element || element.getClientRects().length === 0) {
		return false;
	}

	/*
	 * Un menu che si sta chiudendo resta a schermo per tutta l'animazione, ma
	 * Vuetify gli toglie subito `menuable__content__active`. Senza questo
	 * controllo un menu in chiusura sembrerebbe ancora aperto e il click di
	 * pulizia, invece di chiuderlo, lo riaprirebbe.
	 */
	const content = element.closest('.v-menu__content');

	return !content || content.classList.contains('menuable__content__active');
}

/**
 * Lascia scattare la chiusura che Vuetify ha gia' in coda.
 *
 * I menu si chiudono al primo click fuori, e il click su "Avanti" e' fuori: la
 * direttiva l'ha registrato in cattura, prima ancora che il pulsante facesse il
 * suo lavoro. Riaprendo subito, il menu si richiuderebbe un istante dopo.
 */
function afterPendingClose() {
	return new Promise(resolve => setTimeout(resolve, 0));
}

/**
 * Aspetta che il bersaglio compaia o sparisca.
 *
 * L'attesa sta qui e non nel `timeout` del passo: cosi' si aspetta solo quando
 * c'e' davvero qualcosa da aprire, e a elenco vuoto il passo si aggancia subito
 * in basso invece di far aspettare a vuoto.
 */
async function waitFor(probe, wanted, timeout = 1500) {
	const deadline = performance.now() + timeout;

	while (performance.now() < deadline) {
		if (isVisible(probe) === wanted) {
			return;
		}

		await new Promise(resolve => setTimeout(resolve, 50));
	}
}

function click(selector) {
	const element = document.querySelector(selector);

	if (element) {
		element.click();
	}
}

async function openMenu(activator, probe) {
	await afterPendingClose();

	if (isVisible(probe)) {
		return;
	}

	const element = document.querySelector(activator);

	if (!element) {
		return;
	}

	element.click();
	await waitFor(probe, true);
}

async function closeMenus() {
	await afterPendingClose();

	// L'attivatore fa da interruttore: il secondo click richiude.
	for (const [activator, probe] of [
		[at('row-menu'), ROW_EDIT],
	]) {
		if (isVisible(probe)) {
			click(activator);
			await waitFor(probe, false);
		}
	}
}

const openRowMenu = () => openMenu(at('row-menu'), ROW_EDIT);

/** Richiude i menu a fine giro. */
export function stopReturnsListTour() {
	return closeMenus();
}

export function createReturnsListTourSteps() {
	return [
		{
			body: "Qui ci sono i resi: le richieste dei clienti di rimandare indietro qualcosa, dalla richiesta iniziale fino alla chiusura. Un reso e' sempre agganciato a un ordine, e non e' solo una nota: quando lo si chiude, l'ordine diventa 'Reso', viene registrato l'importo rimborsato e - se lo si chiede - la merce rientra in magazzino.",
			key: 'intro',
			placement: 'bottom',
			target: at('tutorial'),
			title: 'A cosa serve questa pagina',
		},
		{
			body: "La ricerca e' larga e parte da tre caratteri: guarda il numero d'ordine, nome, cognome e email del cliente, e anche sku e titolo dei prodotti resi. Se un cliente scrive lamentandosi, il suo cognome o il codice del prodotto bastano per trovare il reso.",
			before: closeMenus,
			key: 'search',
			placement: 'bottom',
			target: at('search'),
			title: 'Cercare un reso',
		},
		{
			body: "Tre filtri, tutti a scelta multipla e cumulabili: da quale canale arriva l'ordine, con che metodo era stato pagato, e a che punto e' il reso. Quest'ultimo e' quello che si usa tutti i giorni: 'Richiesta di reso' e' la coda di chi aspetta una risposta.",
			key: 'filters',
			placement: 'bottom',
			target: at('filters'),
			title: 'I filtri',
		},
		{
			body: "Ogni riga e' un reso: data, ordine, cliente, le miniature degli articoli resi con il totale, il canale e lo stato. Premi il piu' (o la riga) per aprire il dettaglio: articoli con quantita' e importi, le condizioni della merce (confezione originale, accessori, rientro a magazzino) e il messaggio del cliente.",
			key: 'table',
			placement: 'bottom',
			/*
			 * L'intestazione, non la tabella intera: con un dettaglio aperto la
			 * tabella supera facilmente l'altezza dello schermo, cosi' il ritaglio
			 * coprirebbe tutto il visibile e il velo scuro sembrerebbe sparito.
			 */
			target: `${at('table')} thead`,
			timeout: 250,
			title: 'Come si legge la tabella',
		},
		{
			body: "La lente accanto al numero d'ordine apre l'ordine, non il reso: serve quando devi controllare cosa era stato comprato davvero, come era stato pagato o dove era stato spedito, senza perdere il posto qui.",
			key: 'order-link',
			placement: 'right',
			target: at('order-link'),
			timeout: 250,
			title: "Andare all'ordine",
		},
		{
			body: "Lo stato si legge dal colore, e sono quattro. Blu: richiesta di reso, il cliente ha chiesto e aspetta. Arancio: richiesta accettata, il reso e' autorizzato ma la merce non e' ancora rientrata. Verde: reso completato, la pratica e' chiusa. Rosso: reso non autorizzato, la richiesta e' stata respinta.",
			key: 'status',
			placement: 'left',
			target: at('status'),
			timeout: 250,
			title: 'I quattro stati',
		},
		{
			body: "I tre pallini hanno una voce sola, Modifica, ed e' li' che il reso si fa avanzare: si cambia lo stato, si sistemano le quantita' e gli importi, e si decide se la merce rientra in magazzino. Si apre la pagina del reso, con un tutorial suo; Annulla o Salva riportano qui, alla stessa pagina della lista.",
			before: openRowMenu,
			key: 'row-edit',
			placement: 'right',
			target: ROW_EDIT,
			timeout: 250,
			title: 'Lavorare su un reso',
		},
		{
			body: "Aggiungi reso serve quando il cliente non ha aperto la pratica dal sito ma te l'ha chiesta per telefono o per mail: si cerca il suo ordine, si scelgono le righe che rende e si apre il reso a mano. Il piu' porta alla pagina del nuovo reso, che ha il suo tutorial.",
			key: 'add-return',
			placement: 'left',
			target: at('add'),
			title: 'Aprire un reso a mano',
		},
	];
}
